/**
 * Componente de formulário compartilhado para Configuração de Comissionamento
 *
 * @module App/CommissionConfig/shared/Form
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { CommissionConfigData, CommissionType } from "@/types/commissionConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form as FormComponent } from "@/components/Form";
import { DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { getSelectSellers } from "@/Api/sellers";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { getSelectUnities } from "@/Api/unities";

// Define schema de validação
const formSchema = z.object({
  name: z.string().min(1, { message: "Nome é obrigatório" }),
  start_date: z.string().min(1, { message: "Data inicial é obrigatória" }),
  end_date: z.string().min(1, { message: "Data final é obrigatória" }),
  commission_type_id: z
    .string()
    .min(1, { message: "Tipo de comissão é obrigatório" }),
  ranges: z
    .array(
      z.object({
        initial_value: z
          .string()
          .min(1, { message: "Valor inicial é obrigatório" }),
        final_value: z
          .string()
          .min(1, { message: "Valor final é obrigatório" }),
        values: z
          .array(
            z.object({
              goal_result: z.string(),
              percentage: z
                .string()
                .min(1, { message: "Percentual é obrigatório" }),
            })
          )
          .min(3, { message: "Todos os resultados de meta são obrigatórios" }),
      })
    )
    .min(1, { message: "Pelo menos uma faixa é obrigatória" }),
  sellers: z
    .array(
      z.object({
        id: z.number(),
        target_value: z.string().min(1, { message: "Meta é obrigatória" }),
      })
    )
    .min(1, { message: "Selecione pelo menos um vendedor" }),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  title?: string;
  loading: boolean;
  onSubmit: (values: any) => void;
  buttonText: string;
  initialValues?: CommissionConfigData;
}

type SellerOption = {
  id: number;
  uuid: string;
  user: {
    name: string;
  };
  unity: {
    id: number;
    name: string;
  };
};

type UnityOption = {
  id: number;
  name: string;
};

/**
 * Componente de formulário
 */
export function Form({ loading, onSubmit, buttonText, initialValues }: Props) {
  const [commissionTypes] = useState<CommissionType[]>([
    {
      id: 1,
      name: "Tarifa",
    },
    {
      id: 2,
      name: "Faturamento",
    },
  ]);
  
  const [sellerOptions, setSellerOptions] = useState<SellerOption[]>([]);
  const [unityOptions, setUnityOptions] = useState<UnityOption[]>([]);
  const [selectedUnity, setSelectedUnity] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [openSellersPopover, setOpenSellersPopover] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues
      ? {
          ...initialValues,
          start_date: initialValues.start_date.split("T")?.[0],
          end_date: initialValues.end_date.split("T")?.[0],
          commission_type_id: String(initialValues.commission_type_id),
          ranges: initialValues.commission_ranges.map((range) => ({
            initial_value: Mask.formatCurrency(parseFloat(range.initial_value)),
            final_value: Mask.formatCurrency(parseFloat(range.final_value)),
            values: range.commission_range_values.map((value) => ({
              goal_result: value.goal_result,
              percentage: String(Number(value.percentage) * 100),
            })),
          })),
          sellers: initialValues.sellers.map((seller) => ({
            id: seller.id,
            target_value: Mask.formatCurrency(seller?.pivot?.target_value || 0),
          })),
        }
      : {
          name: "",
          start_date: "",
          end_date: "",
          commission_type_id: "",
          sellers: [],
          ranges: [
            {
              initial_value: "0",
              final_value: "",
              values: [
                { goal_result: "not_hit", percentage: "" },
                { goal_result: "hit", percentage: "" },
                { goal_result: "super_hit", percentage: "" },
              ],
            },
          ],
        },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "ranges",
  });

  const selectedSellers = form.watch("sellers") || [];

  // Carregar unidades
  useEffect(() => {
    const loadUnities = async () => {
      try {
        const { data } = await getSelectUnities();
        if (data?.response) {
          setUnityOptions(data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar unidades:", error);
      }
    };
    
    loadUnities();
  }, []);

  // Carregar vendedores iniciais
  useEffect(() => {
    const loadInitialSellers = async () => {
      try {
        const params: { unity_id?: string; name?: string } = {};
        
        if (selectedUnity) {
          params.unity_id = String(selectedUnity);
        }
        
        if (searchTerm && searchTerm.length >= 2) {
          params.name = searchTerm;
        }
        
        setIsSearching(true);
        const { data } = await getSelectSellers(params);
        if (data?.response) {
          setSellerOptions(data.response);
        }
        setIsSearching(false);
      } catch (error) {
        console.error("Erro ao carregar vendedores:", error);
        setIsSearching(false);
      }
    };
    
    const timeoutId = setTimeout(() => {
      loadInitialSellers();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [selectedUnity, searchTerm]);

  const handleSubmit = (values: FormData) => {
    const formattedValues = {
      ...values,
      commission_type_id: Number(values.commission_type_id),
      ranges: values.ranges.map((range) => ({
        ...range,
        initial_value: Number(
          Mask.transformCurrencyInFloat(range.initial_value)
        ),
        final_value: Number(Mask.transformCurrencyInFloat(range.final_value)),
        values: range.values.map((value) => ({
          ...value,
          percentage: Number(value.percentage) / 100,
        })),
      })),
      sellers: values.sellers.map((seller) => ({
        ...seller,
        target_value: Number(Mask.transformCurrencyInFloat(seller.target_value)),
      })),
    };

    onSubmit(formattedValues);
  };

  const handleRemoveRange = (index: number) => {
    if (fields.length > 1) {
      remove(index);

      // Atualiza os valores iniciais das faixas subsequentes
      if (index < fields.length - 1) {
        const ranges = form.getValues("ranges");
        for (let i = index; i < ranges.length - 1; i++) {
          const nextInitialValue = String(
            Number(ranges[i].final_value.replace(/\D/g, "")) + 1
          );
          form.setValue(`ranges.${i + 1}.initial_value`, nextInitialValue);
        }
      }
    }
  };

  const toggleSeller = (sellerId: number) => {
    const currentSellers = form.getValues("sellers") || [];
    const isSelected = currentSellers.some(seller => seller.id === sellerId);
    
    if (isSelected) {
      form.setValue(
        "sellers",
        currentSellers.filter((seller) => seller.id !== sellerId)
      );
    } else {
      form.setValue("sellers", [
        ...currentSellers,
        { id: sellerId, target_value: "0" }
      ]);
    }
  };

  const selectAllSellers = () => {
    const allSellerIds = sellerOptions.map(seller => ({
      id: seller.id,
      target_value: "0"
    }));
    form.setValue("sellers", allSellerIds);
  };

  const unselectAllSellers = () => {
    form.setValue("sellers", []);
  };

  const getSellerNameById = (id: number) => {
    const seller = sellerOptions.find((seller) => seller.id == id);
    return seller?.user?.name || `Vendedor ${id}`;
  };

  const removeSeller = (sellerId: number) => {
    const currentSellers = form.getValues("sellers") || [];
    form.setValue(
      "sellers",
      currentSellers.filter((seller) => seller.id !== sellerId)
    );
  };

  // const replicateTargetValue = (value: string) => {
  //   const currentSellers = form.getValues("sellers") || [];
  //   const updatedSellers = currentSellers.map(seller => ({
  //     ...seller,
  //     target_value: value
  //   }));
  //   form.setValue("sellers", updatedSellers);
  // };

  const sellersByUnity = sellerOptions.reduce((acc, seller) => {
    const unityId = seller.unity?.id;
    if (!acc[unityId]) {
      acc[unityId] = [];
    }
    acc[unityId].push(seller);
    return acc;
  }, {} as Record<number, SellerOption[]>);

  return (
    <FormComponent onSubmit={form.handleSubmit(handleSubmit)} {...form}>
      <div className="space-y-4">
        <div>
          <FormComponent.Field name="name">
            {({ field }) => (
              <FormComponent.Item>
                <FormComponent.Label>
                  Nome <FormComponent.Message />
                </FormComponent.Label>
                <FormComponent.Control>
                  <Input {...field} placeholder="Nome da configuração" />
                </FormComponent.Control>
              </FormComponent.Item>
            )}
          </FormComponent.Field>
        </div>

        <div>
          <FormComponent.Field name="commission_type_id">
            {({ field }) => (
              <FormComponent.Item>
                <FormComponent.Label>
                  Tipo de Comissão <FormComponent.Message />
                </FormComponent.Label>
                <FormComponent.Control>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {commissionTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormComponent.Control>
              </FormComponent.Item>
            )}
          </FormComponent.Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormComponent.Field name="start_date">
            {({ field }) => (
              <FormComponent.Item>
                <FormComponent.Label>
                  Data Inicial <FormComponent.Message />
                </FormComponent.Label>
                <FormComponent.Control>
                  <Input type="date" {...field} />
                </FormComponent.Control>
              </FormComponent.Item>
            )}
          </FormComponent.Field>

          <FormComponent.Field name="end_date">
            {({ field }) => (
              <FormComponent.Item>
                <FormComponent.Label>
                  Data Final <FormComponent.Message />
                </FormComponent.Label>
                <FormComponent.Control>
                  <Input type="date" {...field} />
                </FormComponent.Control>
              </FormComponent.Item>
            )}
          </FormComponent.Field>
        </div>

        <div>
          <FormComponent.Field name="sellers">
            {() => (
              <FormComponent.Item>
                <FormComponent.Label>
                  Vendedores <FormComponent.Message />
                </FormComponent.Label>
                <div className="space-y-2">
                  <div className="flex gap-2 mb-2">
                    <Select
                      value={selectedUnity ? String(selectedUnity) : "all"}
                      onValueChange={(value) => setSelectedUnity(value === "all" ? null : Number(value))}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Filtrar por unidade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as unidades</SelectItem>
                        {unityOptions.map((unity) => (
                          <SelectItem key={unity.id} value={String(unity.id)}>
                            {unity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Popover
                    open={openSellersPopover}
                    onOpenChange={setOpenSellersPopover}
                  >
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal"
                      >
                        <Search className="mr-2 h-4 w-4" />
                        Selecionar vendedores
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar vendedor..." 
                          value={searchTerm}
                          onValueChange={setSearchTerm}
                        />
                        {isSearching && (
                          <div className="py-6 text-center text-sm">
                            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                            <p className="mt-2">Buscando vendedores...</p>
                          </div>
                        )}
                        {!isSearching && (
                          <CommandList>
                            <CommandEmpty>Nenhum vendedor.</CommandEmpty>
                            
                            <div className="p-2 flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="flex-1"
                                onClick={selectAllSellers}
                                type="button"
                              >
                                Selecionar todos
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="flex-1"
                                onClick={unselectAllSellers}
                                type="button"
                              >
                                Limpar seleção
                              </Button>
                            </div>
                            <CommandSeparator />
                            
                            {Object.entries(sellersByUnity).map(([unityId, sellers]) => {
                              const unityName = sellers[0]?.unity?.name || `Unidade ${unityId}`;
                              return (
                                <div key={unityId}>
                                  <CommandGroup heading={unityName}>
                                    {sellers.map((seller) => {
                                      const isSelected = selectedSellers.some(
                                        s => s.id === seller.id
                                      );
                                      return (
                                        <CommandItem
                                          key={seller.id}
                                          value={seller?.user?.name}
                                          onSelect={() => toggleSeller(seller.id)}
                                        >
                                          <div
                                            className={cn(
                                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                              isSelected
                                                ? "bg-primary text-primary-foreground"
                                                : "opacity-50"
                                            )}
                                          >
                                            {isSelected && (
                                              <Check className="h-4 w-4" />
                                            )}
                                          </div>
                                          <span>{seller?.user?.name}</span>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                  <CommandSeparator />
                                </div>
                              );
                            })}
                          </CommandList>
                        )}
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {selectedSellers.length > 0 && (
                    <div className="space-y-4 mt-4">
                      <div className="grid gap-4">
                        {selectedSellers.map((seller) => (
                          <div key={seller.id} className="flex items-center gap-4 p-3 border rounded-lg">
                            <div className="flex-1">
                              <span className="font-medium">{getSellerNameById(seller.id)}</span>
                            </div>
                            <div className="flex-1">
                              <FormComponent.Field name={`sellers.${selectedSellers.findIndex(s => s.id === seller.id)}.target_value`}>
                                {({ field }) => (
                                  <FormComponent.Item>
                                    <FormComponent.Control>
                                      <Input typeMask="currency" {...field} />
                                    </FormComponent.Control>
                                  </FormComponent.Item>
                                )}
                              </FormComponent.Field>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => removeSeller(seller.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </FormComponent.Item>
            )}
          </FormComponent.Field>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Faixas de Comissão</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                append({
                  initial_value: "",
                  final_value: "",
                  values: [
                    { goal_result: "not_hit", percentage: "" },
                    { goal_result: "hit", percentage: "" },
                    { goal_result: "super_hit", percentage: "" },
                  ],
                })
              }
            >
              <Plus className="h-4 w-4 mr-2" /> Adicionar Faixa
            </Button>
          </div>

          <div className="space-y-4 pr-2">
            {fields.map((field, index) => (
              <div key={field.id} className="p-4 border rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Faixa {index + 1}</h4>
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleRemoveRange(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormComponent.Field name={`ranges.${index}.initial_value`}>
                    {({ field }) => (
                      <FormComponent.Item>
                        <FormComponent.Label>
                          Valor Inicial <FormComponent.Message />
                        </FormComponent.Label>
                        <FormComponent.Control>
                          <Input typeMask="currency" {...field} />
                        </FormComponent.Control>
                      </FormComponent.Item>
                    )}
                  </FormComponent.Field>

                  <FormComponent.Field name={`ranges.${index}.final_value`}>
                    {({ field }) => (
                      <FormComponent.Item>
                        <FormComponent.Label>
                          Valor Final <FormComponent.Message />
                        </FormComponent.Label>
                        <FormComponent.Control>
                          <Input typeMask="currency" {...field} />
                        </FormComponent.Control>
                      </FormComponent.Item>
                    )}
                  </FormComponent.Field>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {["not_hit", "hit", "super_hit"].map(
                    (goalResult, valueIndex) => (
                      <FormComponent.Field
                        key={goalResult}
                        name={`ranges.${index}.values.${valueIndex}.percentage`}
                      >
                        {({ field }) => (
                          <FormComponent.Item>
                            <FormComponent.Label>
                              {goalResult === "not_hit"
                                ? "Meta Não Batida"
                                : goalResult === "hit"
                                ? "Meta Batida"
                                : "Super Meta"}
                              <FormComponent.Message />
                            </FormComponent.Label>
                            <FormComponent.Control>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                placeholder="0%"
                                {...field}
                              />
                            </FormComponent.Control>
                          </FormComponent.Item>
                        )}
                      </FormComponent.Field>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            className={cn("w-full", colors.bg_primary)}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {buttonText}
          </Button>
        </DialogFooter>
      </div>
    </FormComponent>
  );
}
